import { useEffect, useState } from "react";
import {
  Button,
  Icon,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  Textarea,
  UploadButton,
  useToast,
} from "@app/ui";
import {
  createDocumentReference,
  fetchDocumentRaw,
  listDocuments,
  uploadDocument,
  withdrawDocument,
} from "../api/dpp.js";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { useT } from "../i18n.js";
import { DOCUMENT_KINDS } from "../types.js";
import type { AccessGrantRole, DocumentKind, ProductDocument } from "../types.js";
import { AudienceField } from "./AudienceField.js";

/** Server-side rules mirrored for fast client feedback (the API re-enforces both). */
const MAX_BYTES = 20 * 1024 * 1024;
const LANGS = ["tr", "en", "de", "fr", "it", "es", "nl", "pl"] as const;
const PUBLIC_BY_DEFAULT: DocumentKind[] = [
  "instructions",
  "safety_information",
  "declaration_of_conformity",
  "certificate",
];

/**
 * Passport documents: instructions, safety information, declaration of
 * conformity, certificates, test reports, mandates — uploaded as PDF and
 * frozen into the next publish. No delete: revisions supersede, retired
 * documents are withdrawn (published versions keep citing the old bytes).
 */
export function DocumentsCard({
  orgId,
  productId,
  canWrite,
}: {
  orgId: string;
  productId: string;
  canWrite: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const [items, setItems] = useState<ProductDocument[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<DocumentKind>("instructions");
  const [title, setTitle] = useState("");
  const [lang, setLang] = useState<string>("tr");
  const [tier, setTier] = useState<"public" | "restricted">("public");
  const [audience, setAudience] = useState<AccessGrantRole[]>([]);
  const [refOpen, setRefOpen] = useState(false);
  const [refNote, setRefNote] = useState("");
  const [refUrl, setRefUrl] = useState("");
  const [refError, setRefError] = useState<unknown>(null);

  const load = async () => {
    setError(null);
    try {
      setItems(await listDocuments(orgId, productId));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    void load();
  }, [orgId, productId]);

  const chooseKind = (k: DocumentKind) => {
    setKind(k);
    setTier(PUBLIC_BY_DEFAULT.includes(k) ? "public" : "restricted");
  };

  const upload = async (file: File) => {
    if (!title.trim()) {
      toast.error(t("documents.titleRequired"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("documents.tooLarge"));
      return;
    }
    setBusy(true);
    try {
      await uploadDocument(orgId, productId, file, {
        kind,
        title: title.trim(),
        lang,
        tier,
        ...(tier === "restricted" && audience.length > 0 ? { audience } : {}),
      });
      toast.success(t("documents.uploaded"));
      setTitle("");
      await load();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const saveReference = async () => {
    setRefError(null);
    setBusy(true);
    try {
      await createDocumentReference(orgId, productId, {
        kind: "technical_file_reference",
        title: title.trim() || t("documentKind.technical_file_reference"),
        lang,
        tier: "restricted",
        referenceNote: refNote.trim(),
        ...(refUrl.trim() ? { referenceUrl: refUrl.trim() } : {}),
      });
      toast.success(t("documents.uploaded"));
      setRefOpen(false);
      setRefNote("");
      setRefUrl("");
      setTitle("");
      await load();
    } catch (e) {
      setRefError(e);
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id: string) => {
    try {
      await withdrawDocument(orgId, productId, id);
      toast.success(t("documents.withdrawn"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const open = async (d: ProductDocument) => {
    try {
      const blob = await fetchDocumentRaw(orgId, productId, d.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (error != null) return <ErrorAlert error={error} onRetry={() => void load()} />;
  if (items === null) return <Spin />;

  const superseded = new Set(
    items
      .filter((d) => !d.withdrawnAt)
      .map((d) => d.supersedesId)
      .filter(Boolean),
  );
  const statusOf = (d: ProductDocument): "withdrawn" | "superseded" | "current" =>
    d.withdrawnAt ? "withdrawn" : superseded.has(d.id) ? "superseded" : "current";

  return (
    <Space direction="vertical" size="md" style={{ width: "100%" }}>
      <Text small secondary>
        {t("documents.hint")}
      </Text>
      {canWrite && (
        <Space wrap>
          <div style={{ width: 240 }}>
            <Select
              label={t("documents.kind")}
              options={DOCUMENT_KINDS.filter((k) => k !== "technical_file_reference").map((k) => ({
                label: t(`documentKind.${k}`),
                value: k,
              }))}
              value={kind}
              onChange={(v) => chooseKind(v as DocumentKind)}
            />
          </div>
          <Input
            label={t("documents.titleLabel")}
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div style={{ width: 110 }}>
            <Select
              label={t("documents.lang")}
              options={LANGS.map((l) => ({ label: l.toUpperCase(), value: l }))}
              value={lang}
              onChange={(v) => setLang(v)}
            />
          </div>
          <div style={{ width: 150 }}>
            <Select
              label={t("documents.tier")}
              options={[
                { label: t("operators.tier.public"), value: "public" },
                { label: t("operators.tier.restricted"), value: "restricted" },
              ]}
              value={tier}
              onChange={(v) => setTier(v as "public" | "restricted")}
            />
          </div>
          <UploadButton
            accept="application/pdf"
            size="sm"
            disabled={busy}
            icon={<Icon name="plus" size={14} />}
            onSelect={upload}
          >
            {t("documents.upload")}
          </UploadButton>
          <Button size="sm" variant="subtle" disabled={busy} onClick={() => setRefOpen(true)}>
            {t("documents.addReference")}
          </Button>
        </Space>
      )}
      {canWrite && tier === "restricted" && <AudienceField value={audience} onChange={setAudience} />}
      <Table
        rowKey={(d) => d.id}
        empty={t("documents.empty")}
        columns={[
          {
            key: "kind",
            title: t("documents.kind"),
            render: (d: ProductDocument) => t(`documentKind.${d.kind}`),
          },
          { key: "title", title: t("documents.titleLabel"), render: (d: ProductDocument) => d.title },
          { key: "lang", title: t("documents.lang"), render: (d: ProductDocument) => d.lang.toUpperCase() },
          {
            key: "tier",
            title: t("documents.tier"),
            render: (d: ProductDocument) => (
              <Tag color={d.tier === "public" ? "success" : "default"}>{t(`operators.tier.${d.tier}`)}</Tag>
            ),
          },
          {
            key: "size",
            title: t("documents.size"),
            render: (d: ProductDocument) =>
              d.byteSize === null ? t("documents.referenceOnly") : `${Math.round(d.byteSize / 1024)} KB`,
          },
          {
            key: "status",
            title: t("common.status"),
            render: (d: ProductDocument) => {
              const s = statusOf(d);
              return <Tag color={s === "current" ? "primary" : "default"}>{t(`documents.status.${s}`)}</Tag>;
            },
          },
          {
            key: "actions",
            title: "",
            render: (d: ProductDocument) => (
              <Space size="sm">
                {d.byteSize !== null && (
                  <Button size="sm" onClick={() => void open(d)}>
                    {t("documents.open")}
                  </Button>
                )}
                {canWrite && !d.withdrawnAt && (
                  <Popconfirm
                    title={t("documents.withdrawConfirm")}
                    description={t("documents.withdrawDescription")}
                    okType="danger"
                    okText={t("product.withdrawIdentifier")}
                    cancelText={t("common.cancel")}
                    onConfirm={() => void withdraw(d.id)}
                  >
                    <Button size="sm" variant="danger">
                      {t("product.withdrawIdentifier")}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
        data={items}
      />

      <Modal
        open={refOpen}
        onClose={() => setRefOpen(false)}
        title={t("documents.addReference")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        onOk={() => void saveReference()}
        confirmLoading={busy}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {refError != null && <ErrorAlert error={refError} />}
          <Text small secondary>
            {t("documents.referenceHint")}
          </Text>
          <Input label={t("documents.titleLabel")} value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea
            label={t("documents.referenceNote")}
            required
            rows={3}
            value={refNote}
            onChange={(e) => setRefNote(e.target.value)}
          />
          <Input
            label={t("documents.referenceUrl")}
            placeholder="https://"
            value={refUrl}
            onChange={(e) => setRefUrl(e.target.value)}
          />
        </Space>
      </Modal>
    </Space>
  );
}
